/**
 * Dependency-free half of src/copilot/api.js: the GraphQL documents, the
 * variable/cache-key builders, the session-resolution fallback, the
 * "Conversation not found" matcher and the cookie-domain mapper. Kept free of
 * imports so scripts/test-copilot-conversations.mjs can execute the shipped
 * source under node:test (jest is broken repo-wide).
 *
 * Backend contract (oppizi-api PR #7184), both THIRD_PARTY_CLIENT-only:
 *  - talkJsCreateClientConversation(seedMessage, conversationId, fresh):
 *    no args → the account's default conversation (create-or-reuse);
 *    conversationId → reopen that owned thread (foreign/unknown → error
 *    "Conversation not found"); fresh:true → mint a new one (reuses the
 *    caller's most recent EMPTY thread if any).
 *  - talkJsListClientConversations(limit) → most recently active first,
 *    epoch-ms timestamps — already the library's CopilotConversationSummary shape.
 *
 * Deploy-order decoupling: production oppizi-api may still expose only the
 * pre-#7184 `talkJsCreateClientConversation(seedMessage)`. GraphQL validates
 * the whole document, so a document that merely DECLARES `conversationId` /
 * `fresh` is rejected there ("Unknown argument") even when the variables are
 * omitted — which would kill the default thread for every client. Hence two
 * documents: the argument-less one (valid on both schemas) whenever no option
 * is passed, the extended one only when a thread is reopened or minted.
 */

/** The default thread: no arguments, valid against the pre- and post-#7184 schema. */
export const CREATE_DEFAULT_CLIENT_CONVERSATION = `mutation {
  talkJsCreateClientConversation {
    conversationId
    alreadyExists
    token {
      appId
      userId
      signature
    }
  }
}`;

export const CREATE_CLIENT_CONVERSATION = `mutation ($conversationId: String, $fresh: Boolean) {
  talkJsCreateClientConversation(conversationId: $conversationId, fresh: $fresh) {
    conversationId
    alreadyExists
    token {
      appId
      userId
      signature
    }
  }
}`;

/** How many conversations the panel's History view lists. */
export const HISTORY_LIST_LIMIT = 30;

export const LIST_CLIENT_CONVERSATIONS = `query {
  talkJsListClientConversations(limit: ${HISTORY_LIST_LIMIT}) {
    conversationId
    title
    summary
    createdAt
    lastMessageAt
    isDefault
  }
}`;

/**
 * `sessionKey` value meaning "no stored selection → the backend's default
 * conversation" (the README's convention).
 */
export const DEFAULT_SESSION_KEY = "default";

/**
 * Variables for the create mutation. Unset options are omitted (not sent as
 * null) so the backend's defaults apply; `conversationId` and `fresh` are
 * mutually exclusive by contract, so asking for both is a caller bug.
 */
export const buildCreateConversationVariables = ({ conversationId, fresh } = {}) => {
  const variables = {};
  if (conversationId) variables.conversationId = conversationId;
  if (fresh) {
    if (conversationId) throw new Error("createClientConversation: conversationId and fresh are mutually exclusive");
    variables.fresh = true;
  }
  return variables;
};

/**
 * Which create document to send for a set of variables: the argument-less one
 * for the default thread (works on every deployed schema), the extended one
 * only when an argument actually travels.
 */
export const selectCreateConversationDocument = (variables = {}) =>
  Object.keys(variables).length === 0 ? CREATE_DEFAULT_CLIENT_CONVERSATION : CREATE_CLIENT_CONVERSATION;

/** Session cache key: per user AND per conversation ('default' when none). */
export const sessionCacheKey = (userId, conversationId) =>
  `${userId}|${conversationId && conversationId !== DEFAULT_SESSION_KEY ? conversationId : DEFAULT_SESSION_KEY}`;

const NOT_FOUND = /Conversation not found/;
const REOPEN_UNSUPPORTED = /Unknown argument "(conversationId|fresh)"/;

/**
 * The one error that may make the host forget its stored conversation id
 * (README "Switching conversations"): the backend's "Conversation not found".
 * callGraphQLEndpoint rejects with the raw `errors[0]` object — a plain
 * `{ message }`, not an Error — so `String(err)` alone would never match.
 * Everything else (network, 5xx, timeout) must rethrow so the library retries
 * and, failing that, shows its session error with Retry.
 */
const errorMessage = (error) => {
  if (error == null) return "";
  if (typeof error === "string") return error;
  return typeof error.message === "string" ? error.message : String(error);
};

export const isConversationNotFound = (error) => NOT_FOUND.test(errorMessage(error));

/**
 * The backend this page talks to predates #7184 (no `conversationId` /
 * `fresh` arguments yet): the extended document fails GraphQL validation.
 * The selection is NOT wrong, the API is just older — keep it and fall back
 * to the default thread for now.
 */
export const isReopenUnsupported = (error) => REOPEN_UNSUPPORTED.test(errorMessage(error));

/**
 * README recipe for `fetchSession`, as a pure function so it can be unit-tested
 * with fakes: resolve THAT conversation; a thread that no longer resolves
 * ("Conversation not found": deleted, or recorded under a different account)
 * is forgotten (`forgetSelection`) before falling back to the default thread;
 * a backend that cannot reopen threads yet falls back WITHOUT forgetting;
 * anything else (network blip, 5xx, timeout) rethrows so the library retries
 * and, failing that, shows its session error with Retry instead of silently
 * dropping the user's selection.
 *
 * @param {{ conversationId?: string, fetchSession: (id?: string) => Promise<T>, forgetSelection?: (id: string) => void }} args
 * @returns {Promise<T>}
 * @template T
 */
export const resolveClientSession = async ({ conversationId, fetchSession, forgetSelection }) => {
  if (!conversationId || conversationId === DEFAULT_SESSION_KEY) return fetchSession();
  try {
    return await fetchSession(conversationId);
  } catch (error) {
    if (isConversationNotFound(error)) {
      forgetSelection?.(conversationId);
      return fetchSession();
    }
    if (isReopenUnsupported(error)) return fetchSession();
    throw error;
  }
};

/**
 * Library cookie domain from sharedCookieDomain()'s value: '' (localhost /
 * previews / unknown hosts) → undefined, i.e. a host-only cookie; the leading
 * dot is dropped ('.oppizi.com' → 'oppizi.com') so this host writes the very
 * same Domain attribute platform-next does — one scope, per the README.
 */
export const toCopilotCookieDomain = (sharedDomain) => {
  if (!sharedDomain) return undefined;
  return sharedDomain.startsWith(".") ? sharedDomain.slice(1) : sharedDomain;
};
